import { type UseQueryResult } from "@tanstack/react-query";
import type {
  FormSchemaNormalQuestionLookupType,
  FormSchemaType,
  FormSubmissionAnswersInFormType,
  FormSubmissionType,
  GetOneResponseType,
  ResourceAdapterType,
  ResourceLookupType,
  ResourceType,
} from "@validereinc/domain";
import {
  AssetType,
  AttributeDataType,
  DeviceAdapter,
  EquipmentAdapter,
  FacilityAdapter,
  FlowAdapter,
  Resources,
} from "@validereinc/domain";
import { invariant, type RecordValueType } from "@validereinc/utilities";
import isValidDate from "date-fns/isValid";
import { cloneDeep, get, isEmpty, isEqual, isObject } from "es-toolkit/compat";
import { useEffect, useMemo, useRef, useState } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import { usePrevious } from "../../utilities.ts";
import {
  CalculatedFieldService,
  FormCalculatedFieldService,
} from "./calculated_fields.ts";
import {
  areConditionsSatisfied,
  checkIfAnswersToSourceQuestionsChanged,
  findAnswerForQuestion,
  getNextUserChangedFieldPaths,
  getParentQuestionName,
  getValueForQuestionInputFromQuestionValue,
  getValuePathFromQuestionPath,
} from "./util.ts";

type FieldsToOverrideType = Array<{
  lookupAttribute: string;
  fieldName: string;
}>;

type LookupConfigurationType = {
  fieldsToOverride: FieldsToOverrideType;
  entityType?: ResourceLookupType;
};

export type WatchedFields = Record<
  string,
  {
    questionId: string;
    sectionId: string;
    sectionIdx: number;
    lookup?: LookupConfigurationType;
    calculation?: {
      sourceFields: string[];
      sourceFieldValues: Record<string, { value: any; unit?: string }>;
    };
  }
>;

type LookupQueryMetaType = {
  lookupConfiguration: LookupConfigurationType;
  fieldToWatch: string;
  isParentQuery: boolean;
};

/**
  Get a record of form fields to watch which map to a record of configurations that describe why the field is being watched for value changes.

  The key of the return record is the name of the form field to watch. (e.g. "answers.sectionName.sectionIndex.questionId")
  The value is another record where each key is a feature configuration which has associated logic downstream that will use it to process the watched form field.

  One example of this is a lookup field. The key of this configuration is just "lookup".
  e.g. here's what that would look like...
  {
    "answers.sectionName.sectionIndex.questionId": {
      "lookup": {
        fieldsToOverride: { lookupAttribute: string; fieldName: string; }[];
        entityType: AssetTypeType;
      }
    }
  }

  `entityType` is the lookup entity type of the lookup field. @see {@link ResourceLookupType}
  `fieldsToOverride` is an array of all the question form fields that are affected when the watched lookup form field changes. When the lookup form field changes, we resolve it to the entity's details.
  `lookupAttribute` is the specific attribute name on the resolved entity that needs to be extracted.
  `fieldName` is the name of the form field that needs to be updated with the `lookupAttribute`'s value on the resolved entity.

  e.g. here's a complete example...
  {
    "answers.section1.0.facilityQuestion": {
      fieldsToOverride: [
        {lookupAttribute: 'country', fieldName: 'answers.section1.0.facilityCountry'},
        {lookupAttribute: 'is_facility_clean', fieldName: 'answers.section1.0.facilityCleannessBoolean'},
      ],
      entityType: 'facility'
    },
    ...
  }

  so when a user changes the value of `facilityQuestion` form field, the associated facility is fetched (let's call it `fetchedFacility`) and:
    - value of "facilityCountry" is set fetchedFacility.custom_attributes.country
    - value of "facilityCleannessBoolean" is set fetchedFacility.custom_attributes.is_facility_clean
 */
export const useGenerateFieldsToWatch = ({
  formSchema,
  formValues,
}: {
  formSchema?: FormSchemaType;
  formValues: { answers: FormSubmissionAnswersInFormType };
}): { fieldsToWatch: WatchedFields; evaluationOrder: string[] } =>
  useMemo(() => {
    const fieldsToWatch: WatchedFields = {};
    const evaluationOrder: string[] = [];

    if (isEmpty(formValues) || isEmpty(formValues?.answers))
      return { fieldsToWatch, evaluationOrder: [] };

    formSchema?.config.sections.forEach((section) => {
      const evaluation =
        FormCalculatedFieldService.analyzeEquationsOfQuestionsInSection(
          section.questions,
          formSchema.config.questions,
          {
            parser: (eq) => CalculatedFieldService.getVariables(eq),
            skip: (q) => !q.equation,
          }
        );

      evaluationOrder.push(...evaluation.evaluationOrder);

      if (evaluation.evaluationOrder && evaluation.equationDependencies) {
        const dependentFields = Object.entries(evaluation.equationDependencies);

        // for every repeated section
        if (formValues.answers[section.id]) {
          for (
            let sectionIndex = 0;
            sectionIndex < formValues.answers[section.id].length;
            sectionIndex++
          ) {
            dependentFields.forEach(([field, sourceFields]) => {
              const fieldNameToWatch = `answers.${section.id}.${sectionIndex}.${field}`;

              if (fieldsToWatch[fieldNameToWatch] === undefined) {
                fieldsToWatch[fieldNameToWatch] = {
                  questionId: field,
                  sectionId: section.id,
                  sectionIdx: sectionIndex,
                };
              }

              fieldsToWatch[fieldNameToWatch].calculation = {
                sourceFields,
                sourceFieldValues: {},
              };
            });
          }
        }
      }

      section.questions.forEach((questionId) => {
        const question = formSchema.config.questions[questionId];

        if (!formValues.answers?.[section.id]?.length) return;

        for (
          let sectionIndex = 0;
          sectionIndex < formValues.answers[section.id].length;
          sectionIndex++
        ) {
          const fieldPath = `answers.${section.id}.${sectionIndex}.${questionId}`;

          // First matching conditional override wins (same order as useProcessConditionalOverrides)
          let effectiveLookupDefault: {
            lookup_question_id: string;
            lookup_attribute: string;
          } | null = null;

          if (question?.conditional_overrides?.length) {
            for (const override of question.conditional_overrides) {
              const conditionsSatisfied = areConditionsSatisfied({
                conditions: override.conditions,
                values: formValues,
                fieldName: fieldPath,
              });

              if (!conditionsSatisfied) continue;

              // A non-lookup override takes precedence — the lookup pipeline
              // must not register this field so it won't overwrite the value
              // that useProcessConditionalOverrides applies synchronously.
              if (
                !override.default_answer ||
                !isObject(override.default_answer) ||
                !("lookup_question_id" in override.default_answer)
              ) {
                break;
              }

              effectiveLookupDefault = override.default_answer as {
                lookup_question_id: string;
                lookup_attribute: string;
              };
              break;
            }
          }

          if (!effectiveLookupDefault) {
            const baseDefault = question?.default_answer ?? "";

            if (
              isObject(baseDefault) &&
              "lookup_attribute" in baseDefault &&
              "lookup_question_id" in baseDefault
            ) {
              effectiveLookupDefault = baseDefault as {
                lookup_question_id: string;
                lookup_attribute: string;
              };
            }
          }

          if (!effectiveLookupDefault) continue;

          const fieldNameToWatch = getParentQuestionName({
            currentQuestionId: fieldPath,
            formValues,
            questionIdToFind: effectiveLookupDefault.lookup_question_id,
          });

          const fieldToOverride = {
            lookupAttribute: effectiveLookupDefault.lookup_attribute,
            fieldName: fieldPath,
          };

          if (fieldNameToWatch) {
            if (fieldsToWatch[fieldNameToWatch] === undefined) {
              fieldsToWatch[fieldNameToWatch] = {
                questionId,
                sectionId: section.id,
                sectionIdx: sectionIndex,
              };
            }

            if (fieldsToWatch[fieldNameToWatch].lookup) {
              fieldsToWatch[fieldNameToWatch].lookup.fieldsToOverride.push(
                fieldToOverride
              );
            } else {
              const entityType = (
                formSchema.config.questions[
                  effectiveLookupDefault.lookup_question_id
                ] as FormSchemaNormalQuestionLookupType
              )?.lookup_entity_type;

              fieldsToWatch[fieldNameToWatch].lookup = {
                entityType,
                fieldsToOverride: [fieldToOverride],
              };
            }
          }
        }
      });
    });

    return { fieldsToWatch, evaluationOrder };
  }, [formSchema, JSON.stringify(formValues)]);

const resourceToDomainGetOneMap: Partial<
  Record<ResourceType, ResourceAdapterType<any>["getOne"]>
> = {
  [Resources.FACILITY]: FacilityAdapter.getOne,
  [Resources.EQUIPMENT]: EquipmentAdapter.getOne,
  [Resources.DEVICE]: DeviceAdapter.getOne,
  [Resources.FLOW]: FlowAdapter.getOne,
};

/**
 *
 * returns an array of objects in the form of:
 * { queryProps, meta }[] where queryProps are passed to useQueries,
 * and meta that contains information about what fields values should be overridden
 */
export const useGenerateLookupQueries = ({
  watchedFields,
  formValues,
  dirtyFields,
  questionPathsToForcePopulate,
}: {
  formValues: { answers: FormSubmissionAnswersInFormType };
  watchedFields: WatchedFields;
  dirtyFields: object;
  questionPathsToForcePopulate?: string[];
}) => {
  /*
  Only create a query for the fields that are touched.
  This helps with editing a form submission with lookup attribute values with a custom value.
  */
  const [wasTouched, setWasTouched] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const touchedFields: Record<string, boolean> = {};

    // Mark fields that need to be force populated
    if (questionPathsToForcePopulate?.length) {
      questionPathsToForcePopulate.forEach((path) => {
        touchedFields[path] = true;
      });
    }

    // Check for pre-filled lookup questions
    Object.entries(watchedFields).forEach(([field]) => {
      const value = get(formValues, getValuePathFromQuestionPath(field));
      if (value) {
        touchedFields[field] = true;
      }
    });

    if (Object.keys(touchedFields).length > 0) {
      setWasTouched((prev) => ({
        ...prev,
        ...touchedFields,
      }));
    }
  }, [
    JSON.stringify(questionPathsToForcePopulate ?? []),
    JSON.stringify(Object.keys(watchedFields)),
    JSON.stringify(formValues),
  ]);

  useEffect(() => {
    Object.keys(watchedFields).forEach((field) => {
      if (get(dirtyFields, getValuePathFromQuestionPath(field)))
        setWasTouched((prev) => ({ ...prev, [field]: true }));
    });
  }, [JSON.stringify(formValues)]);

  return useMemo(
    () =>
      Object.entries(watchedFields)
        .flatMap(([fieldToWatch, fieldWatchConfiguration]) => {
          // Don't create a query for a field that wasn't even touched!
          if (!wasTouched?.[fieldToWatch]) return;

          const entityIdToFetch = get(
            formValues,
            getValuePathFromQuestionPath(fieldToWatch)
          );

          if (!entityIdToFetch) return;

          const lookupConfiguration = fieldWatchConfiguration.lookup;

          // if the entity type isn't set or the config doesn't exist, exit
          if (!lookupConfiguration?.entityType) return;

          const getOne =
            resourceToDomainGetOneMap?.[lookupConfiguration.entityType];

          // if the query doesn't exist, exit
          if (!getOne) return;

          // Check if any of the attributes are sourced from the parent asset

          const needsParentAssetLookup =
            lookupConfiguration.fieldsToOverride.some(
              (field) =>
                field.lookupAttribute.startsWith("facility.") &&
                lookupConfiguration.entityType !== Resources.FACILITY
            );

          const baseAssetQuery = {
            queryProps: {
              queryKey: [
                lookupConfiguration.entityType,
                "getOne",
                { id: entityIdToFetch },
              ],
              queryFn: () =>
                getOne({
                  id: entityIdToFetch,
                }),
            },
            meta: {
              lookupConfiguration: {
                // Omit any fields that rely on attributes from the parent asset
                // They will be included in the query details of the parent asset
                fieldsToOverride: needsParentAssetLookup
                  ? lookupConfiguration.fieldsToOverride.filter(
                      (field) =>
                        !field.lookupAttribute.startsWith("facility.") &&
                        lookupConfiguration.entityType !== Resources.FACILITY
                    )
                  : lookupConfiguration.fieldsToOverride,
                entityType: lookupConfiguration.entityType,
              },
              fieldToWatch,
              isParentQuery: false,
            } as LookupQueryMetaType,
          };

          // If we don't need to query for the parent asset, just return the base asset query details
          if (!needsParentAssetLookup) return baseAssetQuery;

          const parentAssetQuery = {
            queryProps: {
              queryKey: [
                Resources.FACILITY,
                "getOne",
                { baseAssetId: entityIdToFetch },
              ],
              queryFn: async () => {
                const baseResponse = await getOne({
                  id: entityIdToFetch,
                });

                const parentId =
                  baseResponse.data?.[
                    lookupConfiguration.entityType === Resources.FLOW
                      ? "associated_facility_id"
                      : "facility_id"
                  ];

                invariant(parentId, "No parent ID found");

                return FacilityAdapter.getOne({
                  id: parentId,
                });
              },
            },
            meta: {
              lookupConfiguration: {
                fieldsToOverride: lookupConfiguration.fieldsToOverride
                  .filter(
                    (field) =>
                      field.lookupAttribute.startsWith("facility.") &&
                      lookupConfiguration.entityType !== Resources.FACILITY
                  )
                  .map((field) => ({
                    lookupAttribute: field.lookupAttribute,
                    fieldName: field.fieldName,
                  })),
                entityType: AssetType.FACILITY,
              },
              fieldToWatch,
              isParentQuery: true,
            } as LookupQueryMetaType,
          };

          return [baseAssetQuery, parentAssetQuery];
        })
        // Both of these filters are required for having absolutely zero type warnings and errors:
        .filter((q) => !!q)
        .filter((q) => !!q.queryProps),
    [
      Object.keys(wasTouched).length,
      JSON.stringify(watchedFields),
      Object.keys(watchedFields).map((field) =>
        get(formValues, getValuePathFromQuestionPath(field))
      ),
    ]
  );
};

/**
 * Coerces a date string or Date object to a datetime string.
 * @param date - the date to coerce to a datetime (can be a string or Date object)
 * @returns the datetime string in ISO format
 */
const coerceDateToDateTime = (date: string | Date) => {
  // If it's already a Date object, convert it directly to ISO string
  if (date instanceof Date) {
    return isValidDate(date) ? date.toISOString() : String(date);
  }

  // If it's a string, append "T00:00:00" and convert to ISO string
  const dateObject = new Date(date + "T00:00:00");
  return isValidDate(dateObject) ? dateObject.toISOString() : date;
};

export const useProcessAttributeLookupQueries = ({
  queries,
  queriesDetails,
  watchedFields,
  form,
  formSchema,
  dirtyFields,
}: {
  queries: Array<UseQueryResult<GetOneResponseType<any> | undefined>>;
  queriesDetails: ReturnType<typeof useGenerateLookupQueries>;
  form: UseFormReturn<any>;
  formSchema?: FormSchemaType;
  watchedFields: WatchedFields;
  dirtyFields?: Record<string, unknown>;
}) => {
  const { resolved, fieldsNamesToDisable } = useMemo(() => {
    const fieldsNamesToDisable = new Set<string>();
    const output: Record<string, Record<string, any>> = {};

    // Process queries for base and parent assets
    queries.forEach((query, index) => {
      if (!queriesDetails?.[index]) return;

      const queryDetails = queriesDetails[index].meta;
      const { fieldToWatch, lookupConfiguration } = queryDetails;

      if (!output[fieldToWatch]) {
        output[fieldToWatch] = {};
      }

      lookupConfiguration.fieldsToOverride.forEach((field) => {
        const fieldName = getValuePathFromQuestionPath(field.fieldName);
        if (query.isFetching) {
          fieldsNamesToDisable.add(fieldName);
        } else {
          fieldsNamesToDisable.delete(fieldName);
        }
      });

      if (query.data?.data) {
        lookupConfiguration.fieldsToOverride.forEach((field) => {
          const fieldQuestionId = field.fieldName.split(".").at(-1) ?? "";
          const fieldQuestionConfig =
            formSchema?.config.questions[fieldQuestionId];

          if (!fieldQuestionId || !fieldQuestionConfig) return;

          // For parent asset queries, default answers can come from custom attributes or primary attributes (e.g. id, name)
          // For base asset queries, default answers can only come from custom attributes
          const lookupAttributeSplit = field.lookupAttribute.split(".");
          const isCustomAttribute =
            lookupAttributeSplit.length === 1 ||
            lookupAttributeSplit.includes("custom_attributes");
          let resolvedValue = isCustomAttribute
            ? (get(
                query.data?.data?.custom_attributes,
                lookupAttributeSplit.at(-1) ?? ""
              ) ?? "")
            : (get(
                query.data?.data,
                field.lookupAttribute.replace(
                  `${lookupConfiguration.entityType}.`,
                  ""
                )
              ) ?? "");

          if (fieldQuestionConfig.type === "question") {
            let questionInputValue = getValueForQuestionInputFromQuestionValue(
              fieldQuestionConfig.data_type,
              resolvedValue
            );

            if (fieldQuestionConfig.data_type === AttributeDataType.DATE_TIME) {
              resolvedValue = coerceDateToDateTime(resolvedValue);
              questionInputValue = coerceDateToDateTime(questionInputValue);
            }

            output[fieldToWatch][field.fieldName] = questionInputValue;
          } else {
            output[fieldToWatch][field.fieldName] = resolvedValue;
          }
        });
      }
    });

    return { resolved: output, fieldsNamesToDisable };
  }, [queries, queriesDetails]);

  const resolvedSerialized = JSON.stringify(resolved);

  const previousResolvedSerialized = usePrevious(resolvedSerialized);

  useEffect(() => {
    const previousResolved = JSON.parse(previousResolvedSerialized ?? "{}");

    Object.keys(watchedFields).forEach((fieldKey) => {
      const lookupConfiguration = watchedFields[fieldKey]?.lookup;
      const watchedFieldValuePath = getValuePathFromQuestionPath(fieldKey);
      const isWatchedFieldDirty = get(dirtyFields, watchedFieldValuePath);
      const hasResolvedFieldKey = Boolean(resolved[fieldKey]);

      if (hasResolvedFieldKey) {
        // Also apply overrides if watched fields are updated.
        // E.g. If a lookup question depends on a facility, changing the facility should change the value of the lookup.
        Object.entries(resolved[fieldKey]).forEach(
          ([fieldToOverride, valueToOverride]) => {
            const valuePath = getValuePathFromQuestionPath(fieldToOverride);
            const isFieldDirty = get(dirtyFields, valuePath);

            // When the parent lookup changes, dependent fields can stay
            // dirty or non-empty; still apply if the resolved payload for this path changed.
            const previousOverridesRaw = get(previousResolved, fieldKey);
            const previousOverrides =
              previousOverridesRaw &&
              typeof previousOverridesRaw === "object" &&
              !Array.isArray(previousOverridesRaw)
                ? (previousOverridesRaw as Record<string, unknown>)
                : undefined;
            const previousOverrideValue = previousOverrides?.[fieldToOverride];
            const didResolvedOverrideChange = !isEqual(
              previousOverrideValue,
              valueToOverride
            );
            const currentOverrideValue = get(form.getValues(), valuePath);
            const hasCurrentOverrideValue =
              currentOverrideValue !== "" &&
              currentOverrideValue != null &&
              (!Array.isArray(currentOverrideValue) ||
                currentOverrideValue.length > 0);
            const isFirstResolvedOverride = previousOverrideValue === undefined;

            // Preserve saved submission values on initial mount in edit mode.
            // The parent lookup can be prefilled but untouched, and mount-time
            // query responses should not clobber existing dependent answers.
            if (
              (isFirstResolvedOverride || !didResolvedOverrideChange) &&
              hasCurrentOverrideValue &&
              !isWatchedFieldDirty
            )
              return;

            const shouldApplyResolvedValue =
              didResolvedOverrideChange ||
              (hasCurrentOverrideValue && !isFieldDirty);

            if (shouldApplyResolvedValue) {
              form.setValue(valuePath, valueToOverride, {
                shouldDirty: false, // Don't mark as dirty since this is an automatic default
              });
            }
          }
        );
      } else if (
        !hasResolvedFieldKey &&
        lookupConfiguration?.fieldsToOverride?.length
      ) {
        const previousOverridesRaw = get(previousResolved, fieldKey);
        const previousOverrides =
          previousOverridesRaw &&
          typeof previousOverridesRaw === "object" &&
          !Array.isArray(previousOverridesRaw)
            ? (previousOverridesRaw as Record<string, unknown>)
            : undefined;
        const watchedFieldValuePath = getValuePathFromQuestionPath(fieldKey);
        const currentWatchedValue = get(
          form.getValues(),
          watchedFieldValuePath
        );
        const isWatchedFieldEmpty =
          currentWatchedValue === "" ||
          currentWatchedValue == null ||
          (Array.isArray(currentWatchedValue) &&
            currentWatchedValue.length === 0);
        const hasPreviousOverrides =
          !!previousOverrides && Object.keys(previousOverrides).length > 0;

        if (!isWatchedFieldEmpty || !hasPreviousOverrides) return;

        lookupConfiguration.fieldsToOverride.forEach((field) => {
          const valuePath = getValuePathFromQuestionPath(field.fieldName);
          const previousOverrideValue = previousOverrides?.[field.fieldName];
          const currentOverrideValue = get(form.getValues(), valuePath);

          // Avoid redundant setValue if the dependent was never populated by
          // the lookup or has already been cleared elsewhere.
          if (
            previousOverrideValue === undefined ||
            previousOverrideValue === "" ||
            previousOverrideValue === null ||
            (Array.isArray(previousOverrideValue) &&
              previousOverrideValue.length === 0)
          )
            return;
          if (
            currentOverrideValue === "" ||
            currentOverrideValue == null ||
            (Array.isArray(currentOverrideValue) &&
              currentOverrideValue.length === 0)
          )
            return;

          form.setValue(valuePath, "", {
            shouldDirty: false,
          });
        });
      }
    });
  }, [
    resolvedSerialized,
    previousResolvedSerialized,
    JSON.stringify(Object.keys(watchedFields)),
    JSON.stringify(formSchema),
    JSON.stringify(dirtyFields),
  ]);
  return { fieldsNamesToDisable };
};

type AnswerPathType = `answers.${string}`;

export type PendingFormUpdatesType = {
  value: any;
  options?: Partial<{ shouldValidate: boolean; shouldDirty: boolean }>;
};

export const useProcessCalculatedFields = ({
  evaluationOrder,
  watchedFields,
  formValues,
  formSchema,
}: {
  watchedFields: WatchedFields;
  evaluationOrder: string[];
  formValues: { answers: FormSubmissionAnswersInFormType };
  formSchema?: FormSchemaType;
}): {
  watchedFieldsMap: Record<
    string,
    {
      key: string;
      value: RecordValueType<WatchedFields>;
      areSourcesReady?: boolean;
      isCalculating?: boolean;
      isCalculatable?: boolean;
    }
  >;
  pendingFormUpdates: Array<PendingFormUpdatesType & { path: AnswerPathType }>;
} => {
  if (!formSchema?.config.questions)
    return { watchedFieldsMap: {}, pendingFormUpdates: [] };

  const pendingFormUpdates: Record<AnswerPathType, PendingFormUpdatesType> = {};

  const watchedFieldsMap = Object.entries(watchedFields).reduce<
    Record<
      string,
      {
        key: string;
        value: RecordValueType<WatchedFields>;
        areSourcesReady?: boolean;
        isCalculating?: boolean;
        isCalculatable?: boolean;
      }
    >
  >((map, [k, v]) => {
    map[v.questionId] = { key: k, value: v };
    return map;
  }, {});

  const globalInputs: Record<string, number> = {};
  for (const fieldNameToEvaluate of evaluationOrder) {
    if (!watchedFieldsMap[fieldNameToEvaluate]?.value?.calculation) continue;

    const equation = formSchema.config.questions[fieldNameToEvaluate].equation;

    if (!equation) {
      watchedFieldsMap[fieldNameToEvaluate].isCalculating = false;
      watchedFieldsMap[fieldNameToEvaluate].isCalculatable = false;
      continue;
    }

    const sourceFieldValues =
      watchedFieldsMap[
        fieldNameToEvaluate
      ].value.calculation?.sourceFields.reduce<
        Record<string, { value: any; unit?: string }>
      >((map, fieldName) => {
        const q = formSchema.config.questions[fieldName];

        map[fieldName] = {
          value: findAnswerForQuestion(
            formValues.answers,
            fieldName,
            watchedFieldsMap[fieldNameToEvaluate].value.sectionId,
            watchedFieldsMap[fieldNameToEvaluate].value.sectionIdx,
            {
              ignoreRepeatedSections: false,
            }
          )?.value,
          unit: FormCalculatedFieldService.getMeasurementUnitFromQuestion(
            q,
            false
          ),
        };

        return map;
      }, {}) ?? {};

    if (watchedFieldsMap[fieldNameToEvaluate].value.calculation) {
      watchedFieldsMap[
        fieldNameToEvaluate
      ].value.calculation.sourceFieldValues = sourceFieldValues;
    }

    // calculated field should be evaluated when any of the source fields are
    // dirty and have an evaluatable value
    const shouldEvaluate = Object.values(sourceFieldValues).every(
      (fieldValueAndUnit) =>
        FormCalculatedFieldService.canGetEvaluatuableValueFromAnswer(
          fieldValueAndUnit.value
        )
    );

    watchedFieldsMap[fieldNameToEvaluate].isCalculating = false;

    // don't evaluate this field if none of the source fields have a valid value
    if (!shouldEvaluate) {
      // reset calculated value if it exists, since all source fields have been reset too
      if (
        FormCalculatedFieldService.canGetEvaluatuableValueFromAnswer(
          get(formValues, watchedFieldsMap[fieldNameToEvaluate].key)?.value
        )
      ) {
        pendingFormUpdates[
          `${watchedFieldsMap[fieldNameToEvaluate].key}.value` as AnswerPathType
        ] = {
          value: "",
          options: {
            shouldDirty: false,
          },
        };
      }

      continue;
    }

    watchedFieldsMap[fieldNameToEvaluate].isCalculatable = true;
    watchedFieldsMap[fieldNameToEvaluate].isCalculating = true;

    Object.entries(sourceFieldValues).forEach(
      ([sourceFieldName, sourceFieldAnswerValueAndUnit]) => {
        const associatedQuestion =
          formSchema?.config.questions[sourceFieldName];
        let sourceFieldEvaluatableAnswerValue: number | null = null;

        switch (associatedQuestion?.type) {
          case "measurement": {
            // only consider calculated measurement questions
            if (!associatedQuestion.equation)
              sourceFieldEvaluatableAnswerValue = null;

            switch (associatedQuestion.measurement_type) {
              default:
                // measurements are always numeric
                sourceFieldEvaluatableAnswerValue =
                  FormCalculatedFieldService.getEvaluatableValueFromAnswer(
                    sourceFieldAnswerValueAndUnit.value,
                    "number"
                  );
            }
            break;
          }
          case "question": {
            sourceFieldEvaluatableAnswerValue =
              FormCalculatedFieldService.getEvaluatableValueFromAnswer(
                sourceFieldAnswerValueAndUnit.value,
                associatedQuestion.data_type
              );
            break;
          }
        }

        if (sourceFieldEvaluatableAnswerValue === null) {
          delete globalInputs[sourceFieldName];
        } else {
          globalInputs[sourceFieldName] = sourceFieldEvaluatableAnswerValue;
        }
      }
    );

    if (
      watchedFieldsMap[
        fieldNameToEvaluate
      ].value.calculation?.sourceFields.every((sourceFieldName) =>
        CalculatedFieldService.isEvaluatableValue(globalInputs[sourceFieldName])
      )
    ) {
      watchedFieldsMap[fieldNameToEvaluate].areSourcesReady = true;
    } else {
      watchedFieldsMap[fieldNameToEvaluate].areSourcesReady = false;
      watchedFieldsMap[fieldNameToEvaluate].isCalculating = false;
      continue;
    }

    // Store the raw evaluate() result (same units as the API response).
    // Display conversion happens in MeasurementField via convertAnswerIfNecessary.
    const result = CalculatedFieldService.evaluate(equation, globalInputs);

    // when result is not a number, indicate error in evaluation
    if (isNaN(result)) {
      watchedFieldsMap[fieldNameToEvaluate].isCalculatable = false;
      watchedFieldsMap[fieldNameToEvaluate].isCalculating = false;

      // if the value of the field isn't already NaN
      if (
        !isNaN(
          get(formValues, watchedFieldsMap[fieldNameToEvaluate].key)?.value
        )
      ) {
        // set it to NaN
        pendingFormUpdates[
          `${watchedFieldsMap[fieldNameToEvaluate].key}.value` as AnswerPathType
        ] = {
          value: NaN,
          options: {
            shouldDirty: true,
            shouldValidate: true,
          },
        };
      }
      continue;
    }

    // don't set the result, if it's the same as the previous calculation
    if (
      get(formValues, watchedFieldsMap[fieldNameToEvaluate].key)?.value ===
      result
    ) {
      watchedFieldsMap[fieldNameToEvaluate].isCalculating = false;
      continue;
    }

    pendingFormUpdates[
      `${watchedFieldsMap[fieldNameToEvaluate].key}.value` as AnswerPathType
    ] = {
      value: result,
      options: {
        shouldDirty: true,
        shouldValidate: true,
      },
    };

    watchedFieldsMap[fieldNameToEvaluate].isCalculating = false;
  }

  return {
    watchedFieldsMap,
    pendingFormUpdates: Object.entries(pendingFormUpdates).map(
      ([key, value]) => ({
        path: key as AnswerPathType,
        ...value,
      })
    ),
  };
};

/**
 * Creates a resolved form schema where active conditional overrides are merged into question definitions.
 * This hook evaluates all conditional_overrides for each question based on current form values
 * and returns a new schema with the active overrides applied to question properties.
 *
 * Properties that can be overridden:
 * - equation: Changes the calculation formula for calculated fields
 * - measurement_type/measurement_unit: Affects unit conversions in calculations
 * - minimum/maximum: Affects validation constraints
 * - pick_list_values: Changes available options for picklists
 * - default_answer: Changes the default value (handled separately for form updates)
 * - before/after: Changes the date range for date/date-time questions
 * @param params - The parameters for resolving the schema
 * @param params.formSchema - The base form schema containing question configurations
 * @param params.formValues - The current form values to evaluate conditions against
 * @returns The resolved form schema with active overrides merged, or undefined if no schema provided
 */
export const useResolvedFormSchema = ({
  formSchema,
  formValues,
}: {
  formSchema?: FormSchemaType;
  formValues: { answers: FormSubmissionAnswersInFormType };
}): FormSchemaType | undefined => {
  const resolvedSchema = useMemo(() => {
    if (!formSchema?.config.questions || !formSchema?.config.sections) {
      return formSchema;
    }

    // Create a deep copy of the schema to avoid mutating the original
    const resolved: FormSchemaType = {
      ...formSchema,
      config: {
        ...formSchema.config,
        questions: cloneDeep(formSchema.config.questions),
      },
    };

    // Map to store resolved questions (questionId -> resolved question config)
    // Using 'any' here because we dynamically apply overrides that may not exist on all question types
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const resolvedQuestions: Record<string, any> = {};

    // Iterate through each section to evaluate overrides in context
    formSchema.config.sections.forEach((section) => {
      if (!formValues?.answers?.[section.id]?.length) return;

      // For each section instance (handles repeatable sections)
      for (
        let sectionIndex = 0;
        sectionIndex < formValues.answers[section.id].length;
        sectionIndex++
      ) {
        // Process each question in the section
        section.questions.forEach((questionId) => {
          const question = formSchema.config.questions[questionId];
          if (!question?.conditional_overrides?.length) return;

          const fieldPath = `answers.${section.id}.${sectionIndex}.${questionId}`;

          // Find the first matching override (first match wins)
          for (const override of question.conditional_overrides) {
            const conditionsSatisfied = areConditionsSatisfied({
              conditions: override.conditions,
              values: formValues,
              fieldName: fieldPath,
            });

            if (conditionsSatisfied) {
              // Create or update the resolved question config
              if (!resolvedQuestions[questionId]) {
                resolvedQuestions[questionId] = { ...question };
              }

              // Apply all override properties except default_answer
              // (default_answer is handled by useProcessConditionalOverrides for form state updates)
              if (override.equation !== undefined) {
                resolvedQuestions[questionId].equation = override.equation;
              }
              if (override.measurement_type !== undefined) {
                resolvedQuestions[questionId].measurement_type =
                  override.measurement_type;
              }
              if (override.measurement_unit !== undefined) {
                resolvedQuestions[questionId].measurement_unit =
                  override.measurement_unit;
              }
              if (override.minimum !== undefined) {
                resolvedQuestions[questionId].minimum = override.minimum;
              }
              if (override.maximum !== undefined) {
                resolvedQuestions[questionId].maximum = override.maximum;
              }
              if (override.before !== undefined) {
                resolvedQuestions[questionId].before = override.before;
              }
              if (override.after !== undefined) {
                resolvedQuestions[questionId].after = override.after;
              }
              if (override.pick_list_values !== undefined) {
                resolvedQuestions[questionId].pick_list_values =
                  override.pick_list_values;
              }

              // First matching override wins, stop checking further overrides
              break;
            }
          }
        });
      }
    });

    // Merge resolved questions back into the schema
    Object.keys(resolvedQuestions).forEach((questionId) => {
      resolved.config.questions[questionId] = resolvedQuestions[questionId];
    });

    return resolved;
  }, [formSchema, JSON.stringify(formValues)]);

  return resolvedSchema;
};

/**
 * Tracks the value paths of a form's fields whose latest write came from the user rather than from code.
 *
 * The form library reports writes made through an input's `onChange` with a `change` type, and writes made through
 * `setValue` (defaults, lookups, conditional overrides) without one. A write from code hands the field back to the system.
 * The returned set is replaced, never mutated, whenever it changes.
 *
 * @param form - The form to track
 * @returns Value paths (e.g. "answers.section.0.question.value") whose latest write was by the user
 */
export const useUserChangedFieldPaths = <TFieldValues extends FieldValues>(
  form: Pick<UseFormReturn<TFieldValues>, "watch">
): ReadonlySet<string> => {
  const userChangedFieldPathsRef = useRef<ReadonlySet<string>>(new Set());

  useEffect(() => {
    const subscription = form.watch((_values, { name, type }) => {
      userChangedFieldPathsRef.current = getNextUserChangedFieldPaths({
        userChangedFieldPaths: userChangedFieldPathsRef.current,
        writtenFieldPath: name,
        isWrittenByUser: type === "change",
      });
    });

    return () => subscription.unsubscribe();
  }, [form.watch]);

  return userChangedFieldPathsRef.current;
};

/**
 * Processes conditional overrides for all form questions.
 * When form values change, this hook evaluates conditional_overrides on each question
 * to determine if any question properties (default_answer, min/max, etc.) should be overridden
 * based on the current form state.
 *
 * Applies default_answer overrides when:
 * - The field is not dirty (user hasn't manually changed it), OR
 * - The current value matches the base default (meaning it's still the original default), OR
 * - The answer to a source question has changed
 *
 * ...unless the user last wrote the field's value themselves (see `params.userChangedFieldPaths`) and no source answer
 * has changed since. The dirty flag and the base default comparison both read a user picking the base default again
 * as an untouched field, which would take the choice away from them.
 *
 * @param params - The parameters for processing conditional overrides
 * @param params.formValues - The current form values including all answers
 * @param params.formSchema - The form schema containing question configurations
 * @param params.dirtyFields - Object tracking which fields have been manually edited by the user
 * @param params.userChangedFieldPaths - Value paths (e.g. "answers.section.0.question.value") last written by the user, from `useUserChangedFieldPaths`. Without it, no write is treated as a deliberate user choice.
 * @returns An object containing pendingFormUpdates to be applied to the form
 */
export const useProcessConditionalOverrides = ({
  formValues,
  formSchema,
  dirtyFields,
  userChangedFieldPaths,
}: {
  formValues: { answers: FormSubmissionAnswersInFormType };
  formSchema?: FormSchemaType;
  dirtyFields?: Record<string, unknown>;
  userChangedFieldPaths?: ReadonlySet<string>;
}): {
  pendingFormUpdates: Array<PendingFormUpdatesType & { path: AnswerPathType }>;
} => {
  const previousFormValuesRef = useRef<
    | {
        answers: FormSubmissionAnswersInFormType;
      }
    | undefined
  >(undefined);

  const pendingUpdates = useMemo(() => {
    const updates: Record<AnswerPathType, PendingFormUpdatesType> = {};

    const previousFormValues = previousFormValuesRef.current;

    if (!formSchema?.config.questions || !formSchema?.config.sections) {
      return updates;
    }

    // Iterate through each section
    formSchema.config.sections.forEach((section) => {
      if (!formValues?.answers?.[section.id]?.length) return;

      // Iterate through each section instance (for repeatable sections)
      for (
        let sectionIndex = 0;
        sectionIndex < formValues.answers[section.id].length;
        sectionIndex++
      ) {
        // Iterate through each question in the section
        section.questions.forEach((questionId) => {
          const question = formSchema.config.questions[questionId];
          if (!question?.conditional_overrides?.length) return;

          const fieldPath = `answers.${section.id}.${sectionIndex}.${questionId}`;
          const currentValue = get(formValues, fieldPath);

          let matchingOverrideFound = false;

          // Check each conditional override
          for (const override of question.conditional_overrides) {
            const conditionsSatisfied = areConditionsSatisfied({
              conditions: override.conditions,
              values: formValues,
              fieldName: fieldPath,
            });

            if (!conditionsSatisfied) continue;

            matchingOverrideFound = true;

            // Apply default_answer override if present
            if (override.default_answer !== undefined) {
              const overrideValue = override.default_answer;

              // Lookup-based default answers are resolved by the lookup query pipeline
              // (useGenerateFieldsToWatch → useGenerateLookupQueries → useProcessAttributeLookupQueries)
              if (
                isObject(overrideValue) &&
                "lookup_question_id" in overrideValue
              ) {
                break;
              } else {
                // Handle direct default_answer values
                const isFieldDirty = get(
                  dirtyFields,
                  getValuePathFromQuestionPath(fieldPath)
                );
                const baseDefaultAnswer = question.default_answer;
                const currentActualValue = currentValue?.value;
                const currentValueMatchesBaseDefault =
                  currentActualValue === baseDefaultAnswer;
                const haveAnswersToSourceQuestionsChanged =
                  checkIfAnswersToSourceQuestionsChanged(
                    override.conditions,
                    previousFormValues,
                    formValues,
                    fieldPath
                  );

                const hasUserChangedFieldValue = !!userChangedFieldPaths?.has(
                  getValuePathFromQuestionPath(fieldPath)
                );

                // Apply override if:
                // 1. The answers to the source questions changed (user changed the answer to the conditional override question)
                // 2. The user hasn't deliberately chosen the current value, and either:
                //    a. The field is not dirty (user hasn't manually changed it)
                //    b. The current value matches the base default (meaning it's still the original default)
                const shouldApplyOverride =
                  haveAnswersToSourceQuestionsChanged ||
                  (!hasUserChangedFieldValue &&
                    (!isFieldDirty || currentValueMatchesBaseDefault));
                // And the override value is different from current value
                const shouldUpdate =
                  shouldApplyOverride && currentActualValue !== overrideValue;

                if (shouldUpdate) {
                  updates[fieldPath as AnswerPathType] = {
                    value: { value: overrideValue },
                    options: {
                      shouldValidate: true,
                      shouldDirty: false,
                    },
                  };
                }
              }
            }

            // Once we find a matching override, we don't need to check further overrides
            // (first matching override wins)
            break;
          }

          // If no matching override was found, check if we should revert to base default
          if (!matchingOverrideFound && question.default_answer !== undefined) {
            const isFieldDirty = get(
              dirtyFields,
              getValuePathFromQuestionPath(fieldPath)
            );
            const baseDefaultAnswer = question.default_answer;
            const currentActualValue = currentValue?.value;
            const currentValueMatchesBaseDefault =
              currentActualValue === baseDefaultAnswer;

            // For fields the user cannot edit, the dirty flag is always a system
            // artifact (e.g. set by useFieldArray.append, by an earlier conditional
            // override, by the lookup pipeline). It can never represent a real user
            // edit, so such fields are eligible for revert regardless of dirty state.
            //
            // We deliberately do not mirror the apply branch's source-changed escape
            // hatch here: a source transition cannot be used to clobber a dirty
            // editable field, because the value may be a real user edit that must
            // be preserved when the override deactivates.
            const isUserUneditable = !!question.disable_editing;

            const shouldRevertToBase =
              (!isFieldDirty || isUserUneditable) &&
              !currentValueMatchesBaseDefault;

            if (shouldRevertToBase) {
              updates[fieldPath as AnswerPathType] = {
                value: { value: baseDefaultAnswer },
                options: {
                  shouldValidate: true,
                  shouldDirty: false,
                },
              };
            }
          }
        });
      }
    });

    return updates;
  }, [
    JSON.stringify(formValues),
    formSchema,
    JSON.stringify(dirtyFields),
    JSON.stringify(Array.from(userChangedFieldPaths ?? [])),
  ]);

  // Only update the ref when values actually change
  useEffect(() => {
    if (
      JSON.stringify(formValues) !==
      JSON.stringify(previousFormValuesRef.current)
    ) {
      previousFormValuesRef.current = structuredClone(formValues);
    }
  }, [formValues]);
  const finalUpdates = Object.entries(pendingUpdates).map(([key, value]) => ({
    path: key as AnswerPathType,
    ...value,
  }));

  return {
    pendingFormUpdates: finalUpdates,
  };
};

export const useFieldNamesToDisable = (
  formSchema: FormSchemaType | undefined,
  formValues: {
    answers: FormSubmissionAnswersInFormType;
  },
  defaultValues: Record<string, string> | undefined,
  formSubmission: FormSubmissionType | undefined
): { fieldsNamesToDisable: Set<string> } => {
  const fieldsNamesToDisable = useMemo(() => {
    const set = new Set<string>();
    if (!formSchema) return set;

    formSchema.config.sections.forEach((section) => {
      if (!formValues?.answers?.[section.id]?.length) return;
      for (
        let sectionIndex = 0;
        sectionIndex < formValues.answers[section.id].length;
        sectionIndex++
      ) {
        section.questions.forEach((questionId) => {
          if (!formSchema.config.questions[questionId]?.disable_editing) return;

          set.add(
            getValuePathFromQuestionPath(
              `answers.${section.id}.${sectionIndex}.${questionId}`
            )
          );
        });
      }
    });
    return set;
  }, [formSchema, formSubmission, defaultValues, formValues]);
  return { fieldsNamesToDisable };
};

/**
 * Validates that the current field value exists in the available options.
 * If the value is not found in the options array (and options have finished loading),
 * the field value is automatically cleared. This prevents stale or invalid selections
 * from persisting when the available options change.
 *
 * @param params - The validation parameters
 * @param params.name - The name of the field to validate
 * @param params.options - The array of available options to validate against
 * @param params.value - The current value of the field
 * @param params.isLoading - Whether the options are still being loaded
 * @param params.setValue - Function to update the field value
 */
export const useValidateLookupFieldValue = ({
  options,
  value,
  isLoading,
  setValue,
  name,
}: {
  name: string;
  options?: any[];
  value?: string;
  isLoading: boolean;
  setValue: (name: string, value: any) => void;
}) => {
  useEffect(() => {
    if (
      value &&
      !isLoading &&
      (options?.length === 0 || !options?.some((option) => option.id === value))
    ) {
      setValue(name, "");
    }
  }, [options, value, isLoading, setValue, name]);
};
